/* eslint-disable react/jsx-props-no-spreading */
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';

import useDebounce from '../../hooks/useDebounce';
import useDragProgress from '../../hooks/useDragProgress';
import useKeyboardKeys, { KEYS } from '../../hooks/useKeyboardKeys';
import * as AppPropTypes from '../../lib/PropTypes';

import { useApi } from '../../contexts/ApiContext';
import { useItemRefs } from '../../contexts/ItemRefContext';
import { useList } from '../../contexts/ListsContext';
import PillButton from '../buttons/Pill';
import Arrow from '../icons/Arrow';
import Louve from '../icons/Louve';
import ActiveFiltersButtons from '../partials/ActiveFiltersButtons';
import BookingDetails from '../partials/BookingDetails';
import BoxCarousel from '../partials/BoxCarousel';
import FilmButtons from '../partials/FilmButtons';
import FilmDescription from '../partials/FilmDescription';
import FilmTrailer from '../partials/FilmTrailer';
import SectionHeader from '../partials/SectionHeader';

import styles from '../../../styles/modals/films-modal.module.scss';

const LOAD_EVENTS_DELAY = 1000;

const propTypes = {
    item: AppPropTypes.page.isRequired,
    items: PropTypes.arrayOf(AppPropTypes.page),
    heading: AppPropTypes.page.isRequired,
    onClose: PropTypes.func.isRequired,
    loadNextPage: PropTypes.func,
    listKey: PropTypes.string,
    allLoaded: PropTypes.bool,
    visible: PropTypes.bool.isRequired,
    className: PropTypes.string,
    offset: PropTypes.number,
    withAutoScroll: PropTypes.bool,
};

const defaultProps = {
    items: null,
    loadNextPage: null,
    listKey: null,
    allLoaded: true,
    className: null,
    offset: 80,
    withAutoScroll: false,
};

const FilmsModal = ({
    item: initialItem,
    heading,
    items: initialItems,
    className,
    onClose,
    loadNextPage,
    allLoaded,
    listKey,
    visible,
    offset,
    withAutoScroll,
}) => {
    const intl = useIntl();
    const [item, setItem] = useState(initialItem || {});
    const { listItems } = useList(listKey);
    const api = useApi();
    const items = listItems !== null ? listItems : initialItems;
    const count = (items || []).length;

    const { id: itemId = null, slug: itemSlug } = item || {};
    const {
        title = null,
        description = null,
        url = null,
        type,
        query = null,
        filters = null,
    } = heading || {}; // For indexes

    const { scrollToRef, selectRef } = useItemRefs();

    const finalFilms = useMemo(() => {
        const films =
            type === 'bundle'
                ? items.reduce((all, bundle) => {
                      const { films: bundleFilms = [] } = bundle || {};
                      return all.concat(bundleFilms);
                  }, []) || []
                : items || [];

        return films.map(({ directors = [], ...props }) => ({
            ...props,
            label: (directors || [])
                .map(({ name = null }) => name)
                .filter((n) => n !== null)
                .reduce((s, n) => (s !== null ? `${s}, ${n}` : n), null),
        }));
    }, [type, items]);

    const currentIndex = finalFilms.findIndex(({ id = null }) => id === itemId);

    const onClick = useCallback(
        (newItem, index = 0) => {
            if (newItem !== null) {
                setItem(newItem);
            }
            if (!allLoaded && index > count - 2) {
                loadNextPage();
            }
            if (withAutoScroll) {
                scrollToRef(newItem, offset);
            }
        },
        [
            finalFilms,
            setItem,
            scrollToRef,
            selectRef,
            allLoaded,
            loadNextPage,
            offset,
            withAutoScroll,
            count,
        ],
    );

    const onClickNext = useCallback(
        (e) => {
            e.stopPropagation();
            if (currentIndex < items.length - 1) {
                onClick(items[currentIndex + 1], currentIndex);
            } else {
                onClick(items[0], 0);
            }
        },
        [onClick, items, currentIndex],
    );

    const onClickPrevious = useCallback(
        (e) => {
            e.stopPropagation();
            if (currentIndex > 0) {
                onClick(items[currentIndex - 1], currentIndex);
            } else {
                onClick(items[items.length - 1], items.length - 1);
            }
        },
        [onClick, items, currentIndex],
    );

    const computeDragProgress = useCallback(
        ({ active, movement: [mx], velocities: [vx] }) => {
            const p = mx / (window.innerWidth * 0.7); // drag "ratio": how much of the screen width has been swiped?
            const forwards = mx < 0; // true if swiping to left (to navigate forwards)
            const newIndex = !forwards ? currentIndex - 1 : currentIndex + 1; // which item index are we moving towards?
            const reachedThreshold = Math.abs(vx) > 0.3 || Math.abs(p) > 0.3;
            const reachedBounds = newIndex < 0 || newIndex >= finalFilms.length; // have we reached the end of the stack?
            const damper = reachedBounds ? 0.1 : 1;
            const progress = Math.max(-1, Math.min(1, p * damper));

            if (!active) {
                return reachedThreshold && !reachedBounds ? newIndex : currentIndex;
            }
            return currentIndex - progress;
        },
        [finalFilms, currentIndex],
    );

    const onDragProgress = useCallback(
        (progress, gestureState) => {
            const { active = false } = gestureState || {};
            const delta = Math.abs(progress - currentIndex);
            const reachedBounds = progress < 0 || progress >= finalFilms.length; // have we reached the end of the stack?
            if (!active && delta === 1 && !reachedBounds) {
                setItem(finalFilms[progress]);
            }
        },
        [finalFilms, currentIndex, setItem],
    );

    const { bind, progress: dragProgress } = useDragProgress({
        progress: currentIndex,
        computeProgress: computeDragProgress,
        onProgress: onDragProgress,
        springParams: { config: { tension: 300, friction: 35 } },
    });

    const neighborScreensActive = 2;
    const neighborScreenScale = 1;
    const neighborScreenOffset = 4; // rem
    const neighborScreenOpacity = 0.25;
    const getCarouselStylesByIndex = (index, spring) => ({
        pointerEvents: spring.to((progress) => {
            const t = index - progress;
            return t === 0 ? 'auto' : 'none';
        }),
        opacity: spring.to((progress) => {
            const t = index - progress;
            if (Math.abs(t) > neighborScreensActive) return 0;
            const clamped = Math.min(1, Math.max(0, Math.abs(t)));
            return 1 - (1 - neighborScreenOpacity) * clamped;
        }),
        transform: spring.to((progress) => {
            const t = index - progress;
            if (Math.abs(t) > neighborScreensActive) return `translate(100%)`;
            const clamped = Math.min(1, Math.max(0, Math.abs(t)));
            return `translateX(${t * 100}%) translateX(${t * neighborScreenOffset}rem) scale(${
                1 - (1 - neighborScreenScale) * clamped
            })`;
        }),
    });

    useKeyboardKeys({
        [KEYS.ARROW_LEFT]: onClickPrevious,
        [KEYS.ARROW_RIGHT]: onClickNext,
        [KEYS.ESCAPE]: onClose,
    });

    useEffect(() => {
        selectRef(item);
    }, [item, scrollToRef, selectRef]);

    const [events, setEvents] = useState(null);
    const [isLoadingEvents, setIsLoadingEvents] = useState(true);
    const fetchEvents = useCallback(
        (slug) => {
            api.films.find(slug).then(({ film = null }) => {
                const { screenings = null } = film || {};
                setEvents(screenings);
                setIsLoadingEvents(false);
            });
        },
        [api, setEvents, setIsLoadingEvents],
    );

    useDebounce(fetchEvents, itemSlug, LOAD_EVENTS_DELAY);

    useEffect(() => {
        setIsLoadingEvents(true);
    }, [itemSlug, setIsLoadingEvents]);

    const hasLoadedEvents = events !== null && events.length > 0 && !isLoadingEvents;

    const filmsObject = useMemo(
        () =>
            finalFilms.reduce((acc, film, i) => {
                acc[i] = film;
                return acc;
            }, {}),
        [finalFilms],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.visible]: visible,
                    [styles.hasLoadedEvents]: hasLoadedEvents,
                    [styles.isLoadingEvents]: isLoadingEvents,
                },
            ])}
        >
            <div className={styles.header}>
                <SectionHeader
                    className={styles.sectionHeader}
                    title={title}
                    description={description}
                    url={url}
                    buttonLabel={
                        type === 'section'
                            ? intl.formatMessage({
                                  defaultMessage: 'View section',
                                  description: 'Button label',
                              })
                            : null
                    }
                    onClose={onClose}
                    isDark
                >
                    {query !== null && filters !== null ? (
                        <ActiveFiltersButtons
                            className={styles.activeFilters}
                            buttonClassName={styles.activeFiltersBtn}
                            filters={filters}
                            query={query}
                            isDark
                        />
                    ) : null}
                </SectionHeader>
            </div>
            <div className={styles.trailerCarouselContainer}>
                <BoxCarousel
                    className={styles.titlesCarousel}
                    items={finalFilms}
                    onClick={onClick}
                    currentIndex={currentIndex}
                    progressSpring={dragProgress}
                />
                <div
                    className={styles.trailerCarousel}
                    onContextMenu={(e) => e.preventDefault()}
                    {...bind()}
                >
                    {Object.keys(filmsObject).map((idx, i) => {
                        const film = filmsObject[idx];
                        const { id: filmId = null } = film || {};
                        const isActive = currentIndex + 2 > i && currentIndex - 2 < i;
                        const animatedStyles = isActive
                            ? getCarouselStylesByIndex(i, dragProgress)
                            : { display: 'none' };
                        const imageOnly = currentIndex !== i;
                        return (
                            <animated.div
                                key={`films-${filmId}`}
                                className={classNames([
                                    styles.trailerContainer,
                                    { [styles.isCurrent]: currentIndex === i },
                                ])}
                                style={animatedStyles}
                            >
                                <FilmTrailer
                                    className={styles.trailer}
                                    item={film}
                                    onClose={onClose}
                                    imageOnly={imageOnly}
                                    trailerOnly
                                    withOverlayTitle
                                />
                            </animated.div>
                        );
                    })}
                    {/* @todo #buttons-refactor */}
                    {finalFilms !== null && finalFilms.length > 1 ? (
                        <>
                            <PillButton
                                className={classNames(styles.navButton, styles.navButtonPrevious)}
                                onClick={onClickPrevious}
                                icon={<Arrow direction="left" />}
                            />
                            <PillButton
                                className={classNames(styles.navButton, styles.navButtonNext)}
                                onClick={onClickNext}
                                icon={<Arrow direction="right" />}
                            />
                        </>
                    ) : null}
                </div>
            </div>
            <div className={styles.footer}>
                <div className={styles.descriptionContainer}>
                    <FilmDescription item={item} isDark />
                    <div className={styles.screenings}>
                        <Louve className={styles.loading} loading />
                        <div className={styles.booking}>
                            {hasLoadedEvents ? (
                                <BookingDetails
                                    className={styles.bookingDetails}
                                    events={events}
                                    isDark
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
                <FilmButtons
                    item={item}
                    className={styles.buttons}
                    onClose={onClose}
                    withoutBuyTickets
                />
            </div>
        </div>
    );
};

FilmsModal.propTypes = propTypes;
FilmsModal.defaultProps = defaultProps;

export default FilmsModal;
